import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, LayoutGrid, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { CompanyBrainPillar } from '../components/alt/CompanyBrainPillar';

// Order carries the argument: connect the tools, build with them, take that
// intelligence to where people work, and let it compound.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'platform',
  label: 'Platform',
  icon: LayoutGrid
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const lastScrollYRef = useRef(0);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  useEffect(() => {
    const sections = SECTIONS.map(section => document.getElementById(section.id)).filter((section): section is HTMLElement => Boolean(section));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const scrollingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;
      const entering = entries.filter(entry => entry.isIntersecting).sort((a, b) => SECTIONS.findIndex(section => section.id === a.target.id) - SECTIONS.findIndex(section => section.id === b.target.id));
      const next = scrollingDown ? entering[entering.length - 1] : entering[0];
      if (next?.target.id) {
        setActiveSection(next.target.id as SectionId);
      }
    }, {
      rootMargin: '-32% 0px -67% 0px',
      threshold: 0
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5927" data-mp="1wsv506p7c60k">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5928" data-mp="hd4gr7a4kfl0">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5929" data-mp="cczz3dxhdet7">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5930" data-mp="ceugii9jmosj">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5931" data-mp="2zm8nxvybndcj">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5932" data-mp="34md8bx2cos81">
                <AnimatePresence mode="wait" initial={false} data-id="element-5933" data-mp="39k526wmk2oev">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5934" data-mp="3dli5ayy9kdi3">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5935" data-mp="1umakiv73xgon" />
              that want to <HeadlineIcon icon={Zap} data-id="element-5936" data-mp="1ul96chusoe2p" />
              <UnderscoredPhrase data-id="element-5937" data-mp="1uk6ea6lqk5l7">Go Faster</UnderscoredPhrase>
              <br data-id="element-5938" data-mp="1por0tfrn4bpb" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-5939" data-mp="1pld00bymkajz" />
              <UnderscoredPhrase data-id="element-5940" data-mp="1ll2wr727vy8t">Stay in Control</UnderscoredPhrase>
            </h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5941" data-mp="1gm9fr4nq1p3w">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5942" data-mp="1bo7zsslle5wn">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5943" data-mp="xxb6ezjudkoj">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5944" data-mp="4eok461k3fil" />
                <span className="min-w-0 leading-tight" data-id="element-5945" data-mp="x0zhl7tv90b6">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5946" data-mp="120c57uskabet">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5947" data-mp="11yr8byirygds">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5948" data-mp="nawgekclcg1r">
            <ConnectedAppsPillar data-id="element-5949" data-mp="s97oapjbsghc" />
            <div className="mt-20 md:mt-28" data-id="element-5950" data-mp="s8g74jqh6lal">
              <h3 className="mb-8 font-title text-3xl font-bold leading-tight tracking-tight text-[#2B211A] md:text-4xl" data-id="element-5951" data-mp="1fzbqcsgv8868">
                Why not just use OpenAI or Anthropic?
              </h3>
              <ConnectedAppsTable data-id="element-5952" data-mp="35gfpck54e1b9" />
            </div>
          </div>
        </section>

        <section id="platform" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-5953" data-mp="30hw1ge6yufg9">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5954" data-mp="3f7abzpma32x8">
            <PlatformPillar data-id="element-5955" data-mp="3f8vafqbpr60d" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5956" data-mp="3a90h7g4gu4c5">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5957" data-mp="2mid6yo72v2mf">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-5958" data-mp="2hjtfy9dmn0n7">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-5959" data-mp="2viji43fazqif">
                Everywhere your team works
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-5960" data-mp="2rfwk1ks1h9cs">
                Your AI doesn't have to
                <br data-id="element-5961" data-mp="6pxefhygvcdc" />
                live in another app.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-5962" data-mp="209xwt2k355nd">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            <div className="grid items-center gap-10 md:grid-cols-[1.2fr_0.8fr]" data-id="element-5963" data-mp="2ezwul47qaf2b">
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-5964" data-mp="2ize2036ptma4" />

              <div data-id="element-5965" data-mp="2nxz11za8dzwz">
                <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden md:flex" tabClassName="!px-4 !py-2.5 !text-base" data-id="element-5966" data-mp="2nxfneurm5xor" />

                <div className="space-y-4 font-title text-xl text-[#2B211A] md:text-2xl" style={{
                fontWeight: 500
              }} data-id="element-5967" data-mp="2xw56gqv8xo6l">
                  <IconBullet icon={PlugZap} label="Reads and writes in your connected tools" data-id="element-5968" data-mp="13jfbe5t568xm" />
                  <IconBullet icon={Zap} label="Real work: audits, analysis, reports, presentations" data-id="element-5969" data-mp="3gtotjass2lwl" />
                  <IconBullet icon={Sparkles} label="Autonomous and proactive" data-id="element-5970" data-mp="3c2zy5kdz94s6" />
                </div>
                <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-5971" data-mp="38151wuawlhl9">
                  The co worker is one interface into the platform — not a
                  separate product with its own setup to maintain.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-5972" data-mp="3vqpmfhmpgmwn">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5973" data-mp="3qrcyetk3l9sh">
            <CompanyBrainPillar data-id="element-5974" data-mp="3qtpfl4i17g4h" />
          </div>
        </section>

        <section className="bg-[#FAF8F4] py-20 md:py-28" data-id="element-5975" data-mp="3ls04jout72ge">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5976" data-mp="2fcfu6cfcee0j">
            <AccelerationControlSection data-id="element-5977" data-mp="2famp31kdo1un" />
            <ClosingCTA data-id="element-5978" data-mp="2ad38hv07jczj" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="mr-[0.18em] inline-flex size-[0.86em] -translate-y-[0.06em] items-center justify-center rounded-[0.2em] bg-[#EA580C] align-middle" data-id="element-5979" data-mp="1vxf3j776nyz8">
      <Icon className="size-[0.56em] text-white" strokeWidth={2.6} data-id="element-5980" data-mp="25sqj1rqf1a81" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="relative inline-block align-baseline" data-id="element-5981" data-mp="2emby7c6hsthz">
      <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-5982" data-mp="2eltvbwpbrh13" />
      <span className="relative z-10" data-id="element-5983" data-mp="2tikuc4v36qq6">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-5984" data-mp="u6i2o7cb6q1f" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5985" data-mp="3ardfkjsw1j0l">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5986" data-mp="2w1o8k7y46kse" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-5987" data-mp="2n3cchqehausk">
          <MoreThanAnswers embedded data-id="element-5988" data-mp="2r68ubew72mvg" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5989" data-mp="2d6r36svfexix">
        {mobileTabs}
        <CodeTerminal data-id="element-5990" data-mp="bd1nqbq15hju" />
      </div>;
  }
  return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5991" data-mp="becos3s3cb79">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5992" data-mp="gaa54a3cthk1" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-5993" data-mp="9vut8udvs73i">
        <SlackPanel data-id="element-5994" data-mp="4wrn16m5zuty" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5995" data-mp="nv3y4xwmzes6" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-5996" data-mp="1gc6mch1uiys8" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5997" data-mp="1bk6qp0st6s1g" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5998" data-mp="1jwgt4wc3fct0">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-5999" data-mp="1sx56dotnx94u">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6000" data-mp="2gjv5ahapsnl8">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-6001" data-mp="2lhn0bk6noptn">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6002" data-mp="3n9xydt2pw4qv" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-6003" data-mp="3iabkriiz8mhj">
        <TabIcon slug="claude" fallback="C" data-id="element-6004" data-mp="3s0xskqkgc8po" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-6005" data-mp="3s30sq3fbji84">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6006" data-mp="2zcifi9kvs0gz" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-6007" data-mp="22kc32x7veqai">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-6008" data-mp="22ln6gvxelw13" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-6009" data-mp="2cdewxqudlzul">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-6010" data-mp="23ebaqdzwx4bv">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-6011" data-mp="1ygt10le2ml2t" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-6012" data-mp="1apduegf9hhlr">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-6013" data-mp="15rug5gikd4tt">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-6014" data-mp="3mfvduw0qodiw">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-6015" data-mp="3vegx6te1bwkq">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-6016" data-mp="33nap9qs2oukw">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-6017" data-mp="3cgmokggtjn2v">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-6018" data-mp="2fyi2okxcbz2y">
          Get started for free
          <ArrowRight size={17} data-id="element-6019" data-mp="1dd0x9efkuvma" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-6020" data-mp="1ddssup96ctck">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-6021" data-mp="1iaj5f2lrt6t8">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-6022" data-mp="2xrg7vy54sbgq">
      <div className="overflow-x-auto" data-id="element-6023" data-mp="32k9bgiov5dr8">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-6024" data-mp="r2nvkn3r992e">
          <thead data-id="element-6025" data-mp="m4mcw3a8rbrg">
            <tr className="border-b border-neutral-200" data-id="element-6026" data-mp="m55xjrra0e96">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6027" data-mp="m2jplr0h3wqf">
                <span className="sr-only" data-id="element-6028" data-mp="zv9dk0zkbbq6">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6029" data-mp="vtxn5t8v6mgx">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6030" data-mp="35i8j7tna664">
                Claude
              </th>
              <th scope="col" className="w-[18%] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-6031" data-mp="h6ujt5xv1jlo">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6032" data-mp="1silr5lnqw97c">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-6033" data-mp="1nm50okt2pir3">
                <th scope="row" className="px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6034" data-mp="l8gygb3zp79h">
                  <span className="block" data-id="element-6035" data-mp="q5zaiadb02lp">{row.feature}</span>
                  <span className="mt-1 block max-w-xl text-[12.5px] font-normal leading-[1.45] text-[#8A7B70]" data-id="element-6036" data-mp="u8m1ya62blid">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6037" data-mp="2hg27uu97ptm">
                  <ComparisonValue value={row.chatgpt} data-id="element-6038" data-mp="2j0rp61poikm" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6039" data-mp="6k2vm06kpisx">
                  <ComparisonValue value={row.anthropic} data-id="element-6040" data-mp="bi4bkc8pd206" />
                </td>
                <td className="bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A]" data-id="element-6041" data-mp="bg1hnh4fioq6">
                  <ComparisonValue value={row.giga} highlight data-id="element-6042" data-mp="gafelnyqp3wy" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6043" data-mp="3cdn9z00zi4dm">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6044" data-mp="37dioevl7yjgj" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-6045" data-mp="2yexcvke604p9">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6046" data-mp="2tfkfi5po3f8f" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-6047" data-mp="2op422llujtm2">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6048" data-mp="2on14ngid6y7b">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6049" data-mp="2apchu34qlgzm">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6050" data-mp="2ar5ovjj9i385">
        <Icon size={22} strokeWidth={2.4} data-id="element-6051" data-mp="25qrqyubdk9nu" />
      </span>
      {label}
    </p>;
}